import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { deleteProfilePhoto, uploadProfilePhoto } from "./api/authApi";
import { useAuthUser } from "./auth/AuthUserContext";
import { sanitizeApiMessage } from "./auth/clientAuth";

function Account() {
  const me = useAuthUser();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handlePickClick = () => {
    setError("");
    fileRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setError("");
    setBusy(true);
    try {
      const { response, data } = await uploadProfilePhoto(file);
      if (!response.ok) {
        const raw =
          (data && typeof data === "object" && typeof data.message === "string" && data.message) ||
          (typeof data === "string" && data) ||
          "Upload failed.";
        setError(sanitizeApiMessage(String(raw)));
        return;
      }
      window.dispatchEvent(new Event("willplay:auth-refresh"));
    } catch {
      setError("Could not upload. Check your connection.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemovePhoto = async () => {
    setError("");
    setBusy(true);
    try {
      const { response, data } = await deleteProfilePhoto();
      if (!response.ok) {
        const raw =
          (data && typeof data === "object" && typeof data.message === "string" && data.message) ||
          (typeof data === "string" && data) ||
          "Could not remove photo.";
        setError(sanitizeApiMessage(String(raw)));
        return;
      }
      window.dispatchEvent(new Event("willplay:auth-refresh"));
    } catch {
      setError("Could not remove photo.");
    } finally {
      setBusy(false);
    }
  };

  const pic = me?.profileImageUrl;

  return (
    <main className="page">
      <section className="panel account-panel">
        <h1>Account</h1>
        <p className="muted account-lead">Signed in to WillPlay.</p>

        <div className="account-photo-block">
          <div className="account-photo-preview" aria-hidden={!pic}>
            {pic ? (
              <img src={pic} alt="" className="account-photo-img" width={112} height={112} />
            ) : (
              <div className="account-photo-placeholder">
                {(me?.username ?? "?").slice(0, 1).toUpperCase()}
              </div>
            )}
          </div>
          <div className="account-photo-actions">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="visually-hidden-input"
              aria-label="Choose profile photo"
              onChange={(e) => void handleFileChange(e)}
            />
            <button type="button" className="primary-btn small" disabled={busy} onClick={handlePickClick}>
              {busy ? "Working…" : pic ? "Change photo" : "Upload photo"}
            </button>
            {pic ? (
              <button type="button" className="ghost-btn small" disabled={busy} onClick={() => void handleRemovePhoto()}>
                Remove photo
              </button>
            ) : null}
            <p className="muted small account-photo-hint">Upload profile picture</p>
          </div>
        </div>

        {error ? (
          <p className="error-text account-photo-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="account-details">
          <div className="account-field">
            <span className="account-label">Username</span>
            <span className="account-value">{me?.username ?? "—"}</span>
          </div>
          <div className="account-field">
            <span className="account-label">Email</span>
            <span className="account-value">{me?.email?.trim() ? me.email : "—"}</span>
          </div>
        </div>

        <nav className="account-links" aria-label="Account shortcuts">
          <Link className="account-link primary-btn small" to="/my-bookings">
            My bookings
          </Link>
          <Link className="account-link ghost-btn small" to="/matches">
            Find matches
          </Link>
          <Link className="account-link ghost-btn small" to="/tournaments">
            Tournaments
          </Link>
          <Link className="account-link ghost-btn small" to="/chat">
            Messages
          </Link>
        </nav>

        <div className="account-secondary">
          <p className="muted small">
            <Link to="/terms">Terms</Link>
            {" · "}
            <Link to="/privacy">Privacy</Link>
          </p>
        </div>
      </section>
    </main>
  );
}

export default Account;
